import { Icon, type IIconProps } from '@ad-lucy/ui';
import type { StyledBadgeProps } from '@ad-lucy/ui/lib/components/Navbar/components/Item/components/Link';
import type {
  NavBarNodeVariant,
  StyledDotProps,
  Theme,
} from '@modules/Layout/Default/Sidebar/Navbar/types';
import type { ElementType, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled, { css, type keyframes } from 'styled-components';
import { ANIMATION_DURATION, LINK_HEIGHT } from '../../animationCore';

// Group these components together because they share the same same core design

type Keyframes = ReturnType<typeof keyframes>;
type CommonStylesProps = {
  collapsed?: boolean;
};

// Warning: changing the final height of this element directly affects the sidebar animation
// e.g. changing the top or bottom padding/margin values
const commonStyles = css<CommonStylesProps>`
  height: ${LINK_HEIGHT}px;
  width: ${({ collapsed }) => collapsed && `${LINK_HEIGHT}px`};
  padding: ${({ theme, collapsed }) => !collapsed && `0 ${theme?.spacing?.sm}`};
  display: flex;
  align-items: center;
  justify-content: ${({ collapsed }) => collapsed && 'center'};
  border-radius: 6px;

  &:hover {
    cursor: pointer;
  }
`;

type HomeLinkProps = {
  icon?: IIconProps['type'] | string;
};
type TLinkBaseProps<TAsComponent extends ElementType> = {
  asComponent?: TAsComponent;
  icon?: IIconProps['type'];
  badge?: StyledBadgeProps;
  dot?: StyledDotProps;
  text?: string;
  title?: string;
  active?: boolean;
  collapsed?: boolean;
  animation?: Keyframes;
};

type IStyledLinkProps = Omit<TLinkBaseProps<'a'>, 'active'> & {
  $active: boolean;
  $dot?: StyledDotProps;
  $collapsed?: boolean;
  to: string;
  children?: ReactNode;
};

export const HomeLink = styled(({ icon, ...rest }: IStyledLinkProps) => (
  <Link {...rest} />
))<HomeLinkProps>`
  ${commonStyles};

  &:hover {
    background-color: ${({ theme, icon }) => icon && theme?.colors?.gray?._100};
  }
`;

// Background color applied when link is active OR hovered
function getActiveOrHoverLinkBackgroundColor(
  theme?: Theme,
  variant?: NavBarNodeVariant,
) {
  switch (variant) {
    case 'active':
      return theme?.colors?.success?._50;
    case 'draft':
      return theme?.colors?.warning?._50;
    default:
      return theme?.colors?.gray?._100;
  }
}

// Background color applied when link is active AND hovered
function getActiveAndHoverLinkBackgroundColor(
  theme?: Theme,
  variant?: NavBarNodeVariant,
) {
  switch (variant) {
    case 'active':
      return theme?.colors?.success?._100;
    case 'draft':
      return theme?.colors?.warning?._100;
    default:
      return theme?.colors?.gray?._200;
  }
}

// Warning: changing the final height of this element directly affects the sidebar animation
// e.g. changing the top or bottom padding/margin values
export const StyledLink = styled(({ $active, ...rest }: IStyledLinkProps) => (
  <Link {...rest} />
))<{
  $active: boolean;
  variant?: NavBarNodeVariant; // currently using StyledDot.variant type
}>`
  ${commonStyles};
  gap: ${({ theme }) => theme?.spacing?.sm};
  text-decoration: none;
  color: ${({ theme, $active }) =>
    $active ? theme?.colors?.primary?._900 : theme?.colors?.gray?._500};
  background-color: ${({ theme, $active, variant }) =>
    $active ? getActiveOrHoverLinkBackgroundColor(theme, variant) : undefined};

  &:hover {
    color: ${({ theme }) => theme?.colors?.gray?._900};
    background-color: ${({ theme, $active, variant }) =>
      $active
        ? getActiveAndHoverLinkBackgroundColor(theme, variant)
        : getActiveOrHoverLinkBackgroundColor(theme, variant)};
  }
`;

export const StyledButton = styled.button<{ collapsed?: boolean }>`
  ${commonStyles};
  align-self: ${({ collapsed }) => collapsed && 'center'};
  flex: 0 0 auto;
  border: none;
  color: ${({ theme }) => theme?.colors?.gray?._400};
  background-color: transparent;

  &:hover {
    color: ${({ theme }) => theme?.colors?.gray?._600};
    background-color: ${({ theme }) => theme?.colors?.gray?._100};
  }
`;

export const StyledDot = styled.div<StyledDotProps>`
  flex-shrink: 0;
  width: 8px;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  background-color: ${({ variant, theme }) => {
    switch (variant) {
      case 'active':
        return theme?.colors?.success?._500;
      case 'draft':
        return theme?.colors?.warning?._500;
      case 'archived':
        return theme?.colors?.gray._300;
      case 'hidden':
        return undefined;
      default:
        return undefined;
    }
  }};
`;

type LinkIconProps = {
  $animation?: Keyframes;
};

const linkIconAnimation = (props: LinkIconProps) =>
  css<LinkIconProps>`${props.$animation} forwards ${ANIMATION_DURATION}ms linear;`;

export const LinkIcon = styled(Icon)<LinkIconProps>`
  color: ${({ theme }) => theme?.colors?.gray?._500};
  animation: ${linkIconAnimation};
  flex-shrink: 0;
`;

type ButtonIconProps = {
  collapsed?: boolean;
};
export const ButtonIcon = styled(Icon)<ButtonIconProps>`
  color: ${({ theme, collapsed }) =>
    collapsed ? theme?.colors?.gray?._500 : theme?.colors?.gray?._400};
  flex-shrink: 0;
`;

export const EllipsisText = styled.span`
  flex-grow: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
`;
